import {
  TaxCatalogs,
  TaxProfile,
  TaxProfileField,
  TaxProfileInput,
  TaxProfileOwnerType,
} from 'src/@types/TaxProfile';
import { TranslationKeys } from 'src/locales/es';

// Fiscal data is an El Salvador requirement (electronic invoicing). The
// current user payload carries the country name, not its ISO code.
export const TAX_PROFILE_COUNTRY_NAME = 'El Salvador';
export const TAX_PROFILE_COUNTRY_CODE = 'sv';

// Identity documents other than DUI (13) and NIT (36), which have their own
// fields: 02 residence card, 03 passport, 37 other (catalog CAT-022).
export const OTHER_DOCUMENT_TYPE_CODES = ['02', '03', '37'];

const COMMON_FIELDS: TaxProfileField[] = [
  'taxpayer_type',
  'dui',
  'nit',
  'nrc',
  'legal_name',
  'trade_name',
  'economic_activity_code',
  'department_code',
  'municipality_code',
  'district_code',
  'address_line',
];

// Same split as TaxProfile::ORGANIZATION_ATTRIBUTES and CONTACT_ATTRIBUTES in
// the backend: a contact already has its own phone and email, and only
// contacts can be foreigners.
export const TAX_PROFILE_FIELDS: Record<TaxProfileOwnerType, TaxProfileField[]> =
  {
    Organization: [...COMMON_FIELDS, 'phone', 'email'],
    Contact: [...COMMON_FIELDS, 'other_document_type', 'other_document_number'],
  };

const ALL_FIELDS: TaxProfileField[] = [
  ...COMMON_FIELDS,
  'other_document_type',
  'other_document_number',
  'phone',
  'email',
];

export type TaxProfileFormValues = Record<TaxProfileField, string>;

const FIELD_LABEL_KEYS: Record<string, keyof TranslationKeys> = {
  nit_or_dui: 'taxProfileNitOrDui',
  taxpayer_type: 'taxProfileTaxpayerType',
  dui: 'taxProfileDui',
  nit: 'taxProfileNit',
  nrc: 'taxProfileNrc',
  other_document_type: 'taxProfileOtherDocumentType',
  other_document_number: 'taxProfileOtherDocumentNumber',
  legal_name: 'taxProfileLegalName',
  trade_name: 'taxProfileTradeName',
  economic_activity_code: 'taxProfileEconomicActivity',
  department_code: 'taxProfileDepartment',
  municipality_code: 'taxProfileMunicipality',
  district_code: 'taxProfileDistrict',
  address_line: 'taxProfileAddress',
  phone: 'taxProfilePhone',
  email: 'taxProfileEmail',
};

// Translation key for a field name coming from the backend, or undefined when
// the field is not known here yet.
export const fieldLabelKey = (field: string): keyof TranslationKeys | undefined =>
  FIELD_LABEL_KEYS[field];

export const toFormValues = (
  taxProfile?: TaxProfile | null
): TaxProfileFormValues =>
  ALL_FIELDS.reduce(
    (values, field) => ({ ...values, [field]: taxProfile?.[field] ?? '' }),
    {} as TaxProfileFormValues
  );

// The tax authority rejects identity numbers with hyphens (a NIT, and since
// DTE v2.0 also a DUI), so they are sent as digits only, however the user
// typed them. The backend applies the same cleanup.
const IDENTITY_FIELDS: TaxProfileField[] = [
  'dui',
  'nit',
  'nrc',
  'other_document_number',
];

// Only the fields the owner type accepts. Empty values are sent as null, so
// clearing a field in the form also clears it in the backend.
export const toTaxProfileInput = (
  values: TaxProfileFormValues,
  ownerType: TaxProfileOwnerType
): TaxProfileInput =>
  TAX_PROFILE_FIELDS[ownerType].reduce((input, field) => {
    const trimmed = (values[field] ?? '').trim();
    const value = IDENTITY_FIELDS.includes(field)
      ? stripSeparators(trimmed).toUpperCase()
      : trimmed;
    return { ...input, [field]: value === '' ? null : value };
  }, {} as TaxProfileInput);

export const municipalitiesOf = (
  catalogs: TaxCatalogs,
  departmentCode: string
) =>
  departmentCode
    ? catalogs.municipalities.filter(
        (municipality) => municipality.department_code === departmentCode
      )
    : [];

export const districtsOf = (catalogs: TaxCatalogs, departmentCode: string) =>
  departmentCode
    ? catalogs.districts.filter(
        (district) => district.department_code === departmentCode
      )
    : [];

export const otherDocumentTypesOf = (catalogs: TaxCatalogs) =>
  catalogs.identity_document_types.filter((type) =>
    OTHER_DOCUMENT_TYPE_CODES.includes(type.code)
  );

// Identity numbers are often typed with hyphens, dots or spaces.
export const stripSeparators = (value: string) => value.replace(/[\s.-]/g, '');

// The backend answers validation errors with `{ errors: string[] }`, already
// translated to the user's language.
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const errors = (error as { data?: { errors?: unknown } } | undefined)?.data
    ?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join('. ');
  }

  return fallback;
};
